import { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { BellRing, BellOff, Loader2, Send, Smartphone } from "lucide-react";
import { isPushSupported, getPushState, subscribeToPush, unsubscribeFromPush } from "../lib/push";

// Per-user background push: enable on this device + choose categories.
export function PushSettings() {
  const [state, setState] = useState({ supported: true, subscribed: false, permission: "default" });
  const [labels, setLabels] = useState({});
  const [cats, setCats] = useState({});
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const s = await getPushState();
      setState(s);
      const { data } = await api.get("/push/preferences");
      setLabels(data.labels || {});
      setCats(data.categories || {});
    } catch (e) { /* ignore */ }
    finally { setLoaded(true); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  async function enable() {
    setBusy(true);
    try {
      const r = await subscribeToPush();
      if (r.subscribed) { toast.success("Notifications enabled on this device"); }
      else if (r.permission === "denied") { toast.error("Blocked — allow notifications in your browser's site settings"); }
      await refresh();
    } catch (e) { toast.error(e.message || "Could not enable notifications"); }
    finally { setBusy(false); }
  }

  async function disable() {
    setBusy(true);
    try { await unsubscribeFromPush(); toast.success("Notifications turned off on this device"); await refresh(); }
    catch (e) { toast.error("Could not disable"); }
    finally { setBusy(false); }
  }

  async function sendTest() {
    try { await api.post("/push/test"); toast.success("Test sent — check your notifications"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send test"); }
  }

  async function toggleCat(key) {
    const next = { ...cats, [key]: !cats[key] };
    setCats(next);
    try { await api.put("/push/preferences", { categories: next }); }
    catch (e) { toast.error("Could not save"); setCats(cats); }
  }

  if (!isPushSupported()) {
    return (
      <div className="px-4 py-3 text-xs text-[#68665E] border-b border-[#E5E3DB] bg-[#FDFCF8]" data-testid="push-unsupported">
        Background notifications aren't supported in this browser. On iPhone, first tap Share → “Add to Home Screen”, then open the app and enable them here.
      </div>
    );
  }

  return (
    <div className="border-b border-[#E5E3DB] bg-[#FDFCF8]" data-testid="push-settings">
      <div className="px-4 pt-3 pb-2 flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-[#68665E] font-semibold">Phone &amp; desktop alerts</div>
        {state.subscribed && (
          <button onClick={sendTest} className="text-[11px] text-[#C84B31] inline-flex items-center gap-1 hover:opacity-80" data-testid="push-test-btn">
            <Send className="w-3 h-3" /> Test
          </button>
        )}
      </div>

      {!state.subscribed ? (
        <div className="px-4 pb-3">
          <button onClick={enable} disabled={busy} className="btn-primary w-full !py-2 text-sm" data-testid="push-enable-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <BellRing className="w-4 h-4" />} Enable notifications on this device
          </button>
          <p className="text-[11px] text-[#68665E] mt-1.5">Get alerts even when the app is closed. You’ll be asked to tap “Allow” once.</p>
        </div>
      ) : (
        <div className="px-4 pb-2">
          <div className="flex items-center justify-between text-xs text-[#2F6F4E] mb-2">
            <span className="inline-flex items-center gap-1.5"><Smartphone className="w-3.5 h-3.5" /> On for this device</span>
            <button onClick={disable} disabled={busy} className="text-[#8A2526] inline-flex items-center gap-1 hover:opacity-80" data-testid="push-disable-btn">
              <BellOff className="w-3.5 h-3.5" /> Turn off
            </button>
          </div>
          <div className="space-y-1.5 pb-2">
            {Object.entries(labels).map(([key, label]) => (
              <label key={key} className="flex items-center justify-between gap-3 py-1 cursor-pointer" data-testid={`push-cat-${key}`}>
                <span className="text-sm text-[#1A1A18]">{label}</span>
                <input type="checkbox" checked={!!cats[key]} onChange={() => toggleCat(key)}
                  className="w-4 h-4 accent-[#C84B31]" data-testid={`push-cat-toggle-${key}`} />
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default PushSettings;
